<template>
  <div>
    <LayoutPageHeader 
      title="Frequently Asked Questions" 
      background-image="https://cdn-media.geekcafe.com/a0d4d17b-ec8f-4f28-9169-97e3e5bb4d37/person-looking-at-phone-and-at-macbook-pro-1181244_1920x400.jpg" 
    />
    
    <div class="container faq-page">
      <div class="faq-intro">
        <p>Find answers to common questions about our services, process, and technical expertise. Can't find what you're looking for? <button @click="openContactChat('FAQ Inquiry')" class="inline-contact-link">Contact us</button> directly.</p>
      </div>
      
      <div class="faq-section">
        <div class="section-header">
          <div class="icon-wrapper services-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
              <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
            </svg>
          </div>
          <h2>Services</h2>
        </div>
        
        <div class="faq-item">
          <h3>What services do you offer?</h3>
          <p>We specialize in AWS cloud architecture, DevOps engineering, API design, application development, database design, and cloud migration services.</p>
        </div>

        <div class="faq-item">
          <h3>Do you work with startups or only enterprises?</h3>
          <p>We work with businesses of all sizes, from startups to enterprises. Our services scale to meet your needs and budget.</p>
        </div>

        <div class="faq-item">
          <h3>What cloud platforms do you support?</h3>
          <p>We primarily specialize in AWS (Amazon Web Services), with deep expertise across compute, storage, databases, networking, and serverless architectures.</p>
        </div>
      </div>

      <div class="faq-section">
        <div class="section-header">
          <div class="icon-wrapper process-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
            </svg>
          </div>
          <h2>Process</h2>
        </div>
        
        <div class="faq-item">
          <h3>What does your engagement process look like?</h3>
          <p>We start with a discovery call to understand your needs, followed by a proposal. Once approved, we follow our proven methodology of planning, implementation, and optimization.</p>
        </div>

        <div class="faq-item">
          <h3>How long does a typical project take?</h3>
          <p>Project timelines vary based on scope. Small migrations might take 2-4 weeks, while comprehensive cloud architectures can take 2-3 months. We provide detailed timelines during planning.</p>
        </div>

        <div class="faq-item">
          <h3>Do you provide ongoing support?</h3>
          <p>Yes! We offer post-implementation support and can provide ongoing DevOps and infrastructure management services.</p>
        </div>
      </div>

      <div class="faq-section">
        <div class="section-header">
          <div class="icon-wrapper technical-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="16 18 22 12 16 6" />
              <polyline points="8 6 2 12 8 18" />
            </svg>
          </div>
          <h2>Technical</h2>
        </div>
        
        <div class="faq-item">
          <h3>What programming languages do you work with?</h3>
          <p>We primarily work with Python and .NET, with additional experience in Node.js, Go, and other languages as needed.</p>
        </div>

        <div class="faq-item">
          <h3>Do you follow security best practices?</h3>
          <p>Absolutely. Security is built into every solution we design, following AWS Well-Architected Framework principles and industry best practices.</p>
        </div>

        <div class="faq-item">
          <h3>Can you help with cost optimization?</h3>
          <p>Yes! Cost optimization is a core part of our AWS architecture services. We help identify savings opportunities and implement cost-effective solutions.</p>
        </div>
      </div>

      <div class="cta-section">
        <div class="cta-content">
          <h2>Have more questions?</h2>
          <p>We'd love to hear from you. Contact us to discuss your project and how we can help.</p>
          <button class="btn btn-primary" @click="openContactChat('FAQ Inquiry')">Get In Touch</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
const { openChat: openContactChat } = useContactChat()

useHead({
  title: 'FAQ - Frequently Asked Questions | Geek Cafe',
  meta: [
    { name: 'description', content: 'Frequently asked questions about our AWS cloud architecture, DevOps, and application development services.' }
  ],
  script: [
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'What services do you offer?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'We specialize in AWS cloud architecture, DevOps engineering, API design, application development, database design, and cloud migration services.'
            }
          },
          {
            '@type': 'Question',
            name: 'What cloud platforms do you support?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'We primarily specialize in AWS (Amazon Web Services), with deep expertise across compute, storage, databases, networking, and serverless architectures.'
            }
          }
        ]
      })
    }
  ]
})
</script>

<style scoped>
.faq-page {
  padding: 3rem 1rem 4rem;
  max-width: 1000px;
  margin: 0 auto;
}

.faq-intro {
  text-align: center;
  max-width: 700px;
  margin: 0 auto 3rem;
  padding: 1.5rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(102, 126, 234, 0.2);
}

.faq-intro p {
  color: white;
  font-size: 1.1rem;
  line-height: 1.7;
  margin: 0;
}

.faq-intro a {
  color: #fff;
  text-decoration: underline;
  font-weight: 600;
  transition: opacity 0.2s;
}

.faq-intro a:hover {
  opacity: 0.8;
}

.inline-contact-link {
  background: none;
  border: none;
  color: white;
  text-decoration: underline;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  font-size: inherit;
  font-family: inherit;
  transition: opacity 0.2s;
}

.inline-contact-link:hover {
  opacity: 0.8;
}

.faq-section {
  margin-bottom: 3.5rem;
  animation: fadeInUp 0.6s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.section-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 3px solid #f0f0f0;
}

.icon-wrapper {
  width: 50px;
  height: 50px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
}

.icon-wrapper:hover {
  transform: scale(1.1);
}

.services-icon {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.process-icon {
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
  color: white;
}

.technical-icon {
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
  color: white;
}

.section-header h2 {
  font-size: 2rem;
  margin: 0;
  color: #1a202c;
  font-weight: 700;
}

.faq-item {
  background: white;
  padding: 1.75rem;
  margin-bottom: 1.5rem;
  border-radius: 12px;
  border-left: 4px solid #667eea;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
}

.faq-item:hover {
  transform: translateX(8px);
  box-shadow: 0 8px 20px rgba(102, 126, 234, 0.15);
  border-left-color: #764ba2;
}

.faq-item h3 {
  font-size: 1.35rem;
  margin: 0 0 1rem 0;
  color: #1a202c;
  font-weight: 600;
  display: flex;
  align-items: flex-start;
}

.faq-item h3::before {
  content: "Q:";
  display: inline-block;
  margin-right: 0.75rem;
  color: #667eea;
  font-weight: 700;
  font-size: 1.1rem;
}

.faq-item p {
  color: #4a5568;
  line-height: 1.8;
  margin: 0;
  padding-left: 2.25rem;
  font-size: 1.05rem;
}

.cta-section {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 3rem 2rem;
  border-radius: 16px;
  text-align: center;
  margin-top: 4rem;
  box-shadow: 0 15px 40px rgba(102, 126, 234, 0.3);
  position: relative;
  overflow: hidden;
}

.cta-section::before {
  content: "";
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%);
  animation: pulse 4s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% {
    transform: translate(0, 0) scale(1);
  }
  50% {
    transform: translate(10px, 10px) scale(1.05);
  }
}

.cta-content {
  position: relative;
  z-index: 1;
}

.cta-section h2 {
  margin: 0 0 1rem 0;
  color: white;
  font-size: 2.2rem;
  font-weight: 700;
}

.cta-section p {
  color: rgba(255, 255, 255, 0.95);
  margin-bottom: 2rem;
  font-size: 1.15rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
}

.btn-primary {
  display: inline-block;
  padding: 1rem 2.5rem;
  background: white;
  color: #667eea;
  text-decoration: none;
  border-radius: 50px;
  font-weight: 700;
  font-size: 1.1rem;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
  color: #764ba2;
  text-decoration: none;
}

@media (max-width: 768px) {
  .faq-page {
    padding: 2rem 1rem 3rem;
  }

  .faq-intro {
    margin-bottom: 2rem;
  }

  .faq-intro p {
    font-size: 1rem;
  }

  .section-header h2 {
    font-size: 1.6rem;
  }

  .faq-item {
    padding: 1.25rem;
  }

  .faq-item h3 {
    font-size: 1.15rem;
  }

  .faq-item p {
    font-size: 1rem;
    padding-left: 0;
  }

  .faq-item h3::before {
    display: block;
    margin-bottom: 0.5rem;
  }

  .cta-section {
    padding: 2rem 1.5rem;
  }

  .cta-section h2 {
    font-size: 1.75rem;
  }

  .cta-section p {
    font-size: 1rem;
  }
}
</style>
